import {
  addDefaultProps,
  addDisplayName,
  addHandlers,
  addProps,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { FC, ReactElement } from 'react'
import { createUseStyles } from 'react-jss'
import { addUser } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { addWindowScrollPositionThrottled } from 'shared/src/utils/scrollPosition'

import { STICKY_PAGE_HEADER_Z_INDEX } from '../utils/zIndex'
import { IconButton } from './IconButton'
import { getMaxWidth, LAYOUT_PADDING_VERTICAL, Mode } from './Layout'
import { Menu } from './Menu'
import { SIDE_PADDING } from './PageConstants'
import { MAIN_TITLE_STYLE } from './styles'

const TOP_BAR_HEIGHT = 58

interface StyleProps {
  topBarHeight: number
  mode: Mode
}

const useStyles = createUseStyles({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    color: '#232323',
  },
  topStickyLayoutContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#F9F6F4',
    zIndex: STICKY_PAGE_HEADER_Z_INDEX,
  },
  topStickyContainer: {
    width: '100%',
    maxWidth: ({ mode }: StyleProps) => getMaxWidth(mode),
    display: 'flex',
    flexDirection: 'column',
    marginTop: LAYOUT_PADDING_VERTICAL,
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
  },
  topStickyContainerPageScrolled: {
    borderBottom: '1px solid rgba(144, 115, 79, 0.08)',
  },
  topBarContainer: {
    height: TOP_BAR_HEIGHT,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topBarLeftContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  contentContainerBelowTopBar: {
    marginTop: ({ topBarHeight }: StyleProps) => topBarHeight,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    ...MAIN_TITLE_STYLE,
    marginLeft: 8,
    position: 'relative',
    top: -1,
  },
  topBarIcon: {
    marginRight: -10,
    marginLeft: -10,
  },
})

interface Props {
  renderExtraBarContent?: () => ReactElement
  leftButton?: boolean | string
  title?: string
  onBack?: () => void
  className?: string
  topBarHeight?: number
  renderTopBar?: () => ReactElement
}

export const Page: FC<Props> = flowMax(
  addDisplayName('Page'),
  addNavigate,
  addDefaultProps({
    leftButton: true,
    topBarHeight: TOP_BAR_HEIGHT,
  }),
  addStateHandlers(
    { showMenu: false },
    {
      toggleShowMenu:
        ({ showMenu }) =>
        () => ({
          showMenu: !showMenu,
        }),
    }
  ),
  addUser,
  addProps(({ user, topBarHeight }) => ({
    classes: useStyles({
      mode: user.isAdmin ? 'admin' : 'creator',
      topBarHeight,
    }),
  })),
  addWindowScrollPositionThrottled(),
  addProps(({ windowScrollPositionThrottled: { y } }) => ({
    isPageScrolled: y > 0,
  })),
  addHandlers({
    onBack:
      ({ leftButton, navigate, onBack }) =>
      () => {
        if (onBack) {
          onBack()
        }

        if (typeof leftButton === 'string') {
          leftButton === '' ? window.history.back() : navigate(leftButton)
        }
      },
  }),
  addNavigate,
  ({
    onBack,
    renderExtraBarContent,
    showMenu,
    toggleShowMenu,
    leftButton,
    title,
    isPageScrolled,
    renderTopBar,
    children,
    classes,
    className,
  }) => (
    <div className={cx(classes.container, className)}>
      <div className={classes.topStickyLayoutContainer}>
        <div
          className={cx(
            classes.topStickyContainer,
            isPageScrolled && classes.topStickyContainerPageScrolled
          )}
        >
          {renderTopBar ? (
            renderTopBar()
          ) : (
            <div className={classes.topBarContainer}>
              <div className={classes.topBarLeftContainer}>
                {typeof leftButton === 'string' ? (
                  <IconButton
                    name="back"
                    onClick={onBack}
                    className={classes.topBarIcon}
                    addHitSlop
                  />
                ) : (
                  leftButton && (
                    <IconButton
                      name="menu"
                      onClick={toggleShowMenu}
                      className={classes.topBarIcon}
                      addHitSlop
                    />
                  )
                )}
                {title && <span className={classes.title}>{title}</span>}
              </div>
              {renderExtraBarContent?.() ?? null}
            </div>
          )}
        </div>
      </div>
      <div className={classes.contentContainerBelowTopBar}>{children}</div>
      {showMenu && (
        <>
          <Menu onClose={toggleShowMenu} />
        </>
      )}
    </div>
  )
)
